/**
 * 알림톡 템플릿 관리 — 알리고 콘솔 프록시.
 *
 *  - 좌측: 알리고에서 직접 조회한 템플릿 목록 (우리 DB 저장 없음)
 *  - 우측: 선택된 템플릿 상세 + 액션
 *  - 식별자 = 알리고 templtName — BE 트리거 코드와 동일하게 약속
 *    (예: USER_RESERVE_APPROVED, BIZ_PAYMENT_NOTICE 등)
 */
import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Plus, Send, Trash2, Edit3, X, AlertCircle, Check, Pencil } from 'lucide-react';
import {
  kakaoAlimTemplateApi,
  AligoTemplate,
  AligoTemplateUpsertReq,
  INSP_STATUS_LABEL,
  STATUS_LABEL,
} from '../../services/api/kakaoAlimTemplate';

/** 템플릿명 검증 — 3~60자, 앞뒤 공백 제거 후 판단 (한글/영문/숫자/특수문자 허용). */
const NAME_REGEX = /^.{3,60}$/;

export function KakaoAlimTemplatesPage() {
  const [templates, setTemplates] = useState<AligoTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AligoTemplate | null>(null);
  const [editing, setEditing] = useState<AligoTemplate | 'NEW' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const list = await kakaoAlimTemplateApi.list(refresh);
      setTemplates(list);
      if (selected) {
        const stillExists = list.find((t) => t.templtCode === selected.templtCode);
        setSelected(stillExists ?? null);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? '템플릿 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRequestReview = async (t: AligoTemplate) => {
    if (!window.confirm(`"${t.templtName}" 템플릿을 알리고에 검수 요청합니다. (4~5 영업일 소요)`)) return;
    try {
      await kakaoAlimTemplateApi.requestReview(t.templtCode);
      await load(true);
    } catch (e: any) {
      alert(e?.response?.data?.message ?? '검수 요청 실패');
    }
  };

  const handleDelete = async (t: AligoTemplate) => {
    if (!window.confirm(`"${t.templtName}" 템플릿을 삭제합니다. (승인 전 템플릿만 가능)`)) return;
    try {
      await kakaoAlimTemplateApi.delete(t.templtCode);
      await load(true);
      setSelected(null);
    } catch (e: any) {
      alert(e?.response?.data?.message ?? '삭제 실패');
    }
  };

  const inspBadge = (status?: string) => {
    const cls =
      status === 'APR' ? 'bg-green-100 text-green-700' :
      status === 'REQ' ? 'bg-blue-100 text-blue-700' :
      status === 'REJ' ? 'bg-red-100 text-red-700' :
      'bg-gray-100 text-gray-700';
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full ${cls}`}>
        {INSP_STATUS_LABEL[status ?? ''] ?? status ?? '-'}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">알림톡 템플릿</h1>
          <p className="mt-1 text-sm text-gray-500">
            알리고 콘솔에 등록된 템플릿을 직접 조회/등록/수정/검수합니다. 우리 DB 저장 없음.
            <br />
            <span className="text-gray-400">
              ※ 식별자는 템플릿명(templtName) — 우리 시스템 트리거 코드와 일치시켜야 자동 발송 매칭됨.
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => load(true)}
            className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
          >
            <RefreshCw size={14} /> 새로고침
          </button>
          <button
            onClick={() => setEditing('NEW')}
            className="flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus size={14} /> 신규 등록
          </button>
        </div>
      </header>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* 좌측 목록 */}
        <div className="col-span-5 rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-3 py-2 text-xs font-medium text-gray-500">
            전체 {templates.length}건
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">불러오는 중...</div>
          ) : templates.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">
              알리고에 등록된 템플릿이 없습니다.
            </div>
          ) : (
            <ul className="max-h-[70vh] overflow-y-auto">
              {templates.map((t) => (
                <TemplateListItem
                  key={t.templtCode}
                  t={t}
                  selected={selected?.templtCode === t.templtCode}
                  onSelect={() => setSelected(t)}
                  onRenamed={async () => await load(true)}
                  inspBadge={inspBadge}
                />
              ))}
            </ul>
          )}
        </div>

        {/* 우측 상세 */}
        <div className="col-span-7 rounded-lg border border-gray-200 bg-white">
          {!selected ? (
            <div className="py-20 text-center text-sm text-gray-400">
              왼쪽 목록에서 템플릿을 선택해주세요.
            </div>
          ) : (
            <TemplateDetail
              t={selected}
              onEdit={() => setEditing(selected)}
              onReview={() => handleRequestReview(selected)}
              onDelete={() => handleDelete(selected)}
            />
          )}
        </div>
      </div>

      {editing && (
        <UpsertModal
          initial={editing === 'NEW' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load(true);
          }}
        />
      )}
    </div>
  );
}

/**
 * 목록 아이템 — 이름 인라인 편집 지원 (REG/REJ 상태만).
 */
function TemplateListItem({
  t, selected, onSelect, onRenamed, inspBadge,
}: {
  t: AligoTemplate;
  selected: boolean;
  onSelect: () => void;
  onRenamed: () => Promise<void>;
  inspBadge: (s?: string) => React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(t.templtName);
  const [saving, setSaving] = useState(false);
  // 알리고 공식 API 스펙상 modify 는 REG/REJ 만 허용
  const renameable = t.inspStatus === 'REG' || t.inspStatus === 'REJ';

  useEffect(() => setName(t.templtName), [t.templtName]);

  const beginEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!renameable) {
      alert(
        '알리고 API 스펙상 승인(APR) / 심사 중(REQ) 상태에서는 이름을 변경할 수 없습니다.\n\n' +
        '이름을 바꾸려면 알리고 콘솔(smartsms.aligo.in) 에서 직접 처리하거나,\n' +
        '삭제 후 새 이름으로 재등록 → 재검수해주세요.'
      );
      return;
    }
    setName(t.templtName);
    setEditing(true);
  };
  const cancelEdit = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation();
    setEditing(false);
    setName(t.templtName);
  };
  const submit = async (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation();
    const trimmed = name.trim();
    if (!NAME_REGEX.test(trimmed)) {
      alert('이름은 3~60자여야 합니다.');
      return;
    }
    if (trimmed === t.templtName) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await kakaoAlimTemplateApi.rename(t.templtCode, trimmed);
      await onRenamed();
      setEditing(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? '이름 변경 실패';
      if (msg.includes('수정 가능 상태가 아')) {
        alert(
          `이 상태(${t.inspStatus})에서는 알리고 API 로 이름 변경이 불가합니다.\n\n` +
          `알리고 콘솔(smartsms.aligo.in)에서 직접 변경해주세요.\n\n` +
          `원본 오류: ${msg}`
        );
      } else {
        alert(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <li
      onClick={() => !editing && onSelect()}
      className={`group cursor-pointer border-b border-gray-100 px-3 py-2 hover:bg-gray-50 ${
        selected ? 'bg-primary-50' : ''
      } ${t.inspStatus === 'REJ' ? 'border-l-4 border-l-red-400' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit(e);
                  else if (e.key === 'Escape') cancelEdit(e);
                }}
                disabled={saving}
                className="min-w-0 flex-1 rounded border border-blue-300 px-1.5 py-0.5 text-sm"
              />
              <button
                onClick={submit}
                disabled={saving}
                className="rounded p-1 text-green-600 hover:bg-green-50 disabled:opacity-40"
                title="저장 (Enter)"
              >
                <Check size={14} />
              </button>
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                title="취소 (Esc)"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <div className="truncate text-sm font-medium text-gray-900">{t.templtName}</div>
              {renameable && (
                <button
                  onClick={beginEdit}
                  className="rounded p-0.5 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-700 group-hover:opacity-100"
                  title="이름 변경"
                >
                  <Pencil size={12} />
                </button>
              )}
            </div>
          )}
          <div className="truncate text-xs text-gray-400">{t.templtCode}</div>
          {t.inspStatus === 'REJ' && t.comments && t.comments.length > 0 && (
            <div className="mt-0.5 truncate text-xs text-red-600">
              ⚠ {t.comments[t.comments.length - 1].content ?? '반려됨'}
            </div>
          )}
        </div>
        {inspBadge(t.inspStatus)}
      </div>
    </li>
  );
}

/**
 * 심의 코멘트 대화 패널 — 알리고 검수자 코멘트 + 우리 측 답변.
 *
 *  - inspStatus=REJ: 반려 사유 하이라이트
 *  - 하단 입력란으로 검수자에게 회신 가능
 */
function ReviewCommentsPanel({ template }: { template: AligoTemplate }) {
  const isRejected = template.inspStatus === 'REJ';

  const isOwnReply = (status?: string) => status?.toUpperCase() === 'REPLY';

  return (
    <div
      className={`rounded-lg border p-3 ${
        isRejected ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <AlertCircle size={14} className={isRejected ? 'text-red-600' : 'text-gray-600'} />
        <span className={`text-sm font-semibold ${isRejected ? 'text-red-700' : 'text-gray-700'}`}>
          심의 코멘트 ({template.comments?.length ?? 0})
        </span>
        {isRejected && (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">반려됨</span>
        )}
      </div>

      <ul className="space-y-2">
        {template.comments?.map((c, i) => {
          const own = isOwnReply(c.status);
          return (
            <li
              key={i}
              className={`rounded border px-3 py-2 ${
                own
                  ? 'ml-8 border-blue-200 bg-blue-50'
                  : 'mr-8 border-gray-200 bg-white'
              }`}
            >
              <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
                <span className={`font-medium ${own ? 'text-blue-700' : 'text-gray-700'}`}>
                  {own ? '↩ 운영자 답변' : c.userName ?? '검수자'}
                </span>
                {c.regDate && <span>· {c.regDate}</span>}
              </div>
              <div className="whitespace-pre-wrap text-sm text-gray-800">
                {c.content ?? '(사유 없음)'}
              </div>
            </li>
          );
        })}
      </ul>

      {isRejected && (
        <div className="mt-3 rounded border border-gray-200 bg-white px-3 py-2">
          <div className="mb-1 text-xs font-medium text-gray-700">
            💡 반려 사유에 답변하려면
          </div>
          <div className="text-xs text-gray-600 leading-relaxed">
            알리고 공개 API 에는 반려 답변 기능이 없어, 검수자에게 답변하려면{' '}
            <a
              href="https://smartsms.aligo.in/shop/kakaotemplate.html"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:underline"
            >
              알리고 콘솔
            </a>
            에서 직접 답변해주세요.
            <br />
            여기서는 "수정 후 재검수" 로 본문을 수정하여 다시 검수 요청할 수
            있습니다.
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateDetail({
  t, onEdit, onReview, onDelete,
}: { t: AligoTemplate; onEdit: () => void; onReview: () => void; onDelete: () => void }) {
  const editable = t.inspStatus === 'REG' || t.inspStatus === 'REJ';
  // 알리고 공식 API 스펙상 del 은 승인 전(REG/REJ)만 허용
  const deletable = t.inspStatus === 'REG' || t.inspStatus === 'REJ';

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t.templtName}</h2>
          <div className="mt-1 text-xs text-gray-400">
            tpl_code: <code className="text-gray-600">{t.templtCode}</code>
            {t.cdate && <span className="ml-3">등록: {t.cdate}</span>}
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
            {STATUS_LABEL[t.status ?? ''] ?? t.status}
          </span>
          <span className={`px-2 py-0.5 text-xs rounded-full ${
            t.inspStatus === 'APR' ? 'bg-green-100 text-green-700' :
            t.inspStatus === 'REJ' ? 'bg-red-100 text-red-700' :
            'bg-blue-100 text-blue-700'
          }`}>
            {INSP_STATUS_LABEL[t.inspStatus ?? ''] ?? t.inspStatus}
          </span>
        </div>
      </div>

      {/* 심의 코멘트 배너 — 반려(REJ) 상태에서만 표시 */}
      {t.inspStatus === 'REJ' && t.comments && t.comments.length > 0 && (
        <ReviewCommentsPanel template={t} />
      )}

      <div>
        <div className="text-xs font-medium text-gray-500 mb-1">본문</div>
        <pre className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 font-mono">
          {t.templtContent}
        </pre>
      </div>

      {t.buttons && t.buttons.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">버튼 ({t.buttons.length})</div>
          <ul className="space-y-1">
            {t.buttons.map((b, i) => (
              <li key={i} className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700">
                <span className="font-medium">{b.name}</span>
                <span className="ml-2 text-gray-400">[{b.linkTypeName ?? b.linkType}]</span>
                {b.linkMo && <span className="ml-2 text-gray-500">{b.linkMo}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 border-t border-gray-100 pt-3">
        <button
          onClick={onEdit}
          disabled={!editable}
          className={`flex items-center gap-1 rounded px-3 py-1.5 text-sm disabled:opacity-40 ${
            t.inspStatus === 'REJ'
              ? 'bg-orange-500 text-white hover:bg-orange-600'
              : 'border border-gray-300 hover:bg-gray-50'
          }`}
          title={t.inspStatus === 'REJ' ? '수정 후 다시 검수 요청 (수정 없이 그대로 재검수도 가능)' : '수정'}
        >
          <Edit3 size={14} /> {t.inspStatus === 'REJ' ? '수정 후 재검수' : '수정'}
        </button>
        {/* 검수 요청 버튼은 REG 상태에서만 표시. REJ 는 "수정 후 재검수" 로 통합. */}
        {t.inspStatus === 'REG' && (
          <button
            onClick={onReview}
            className="flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
            title="검수 요청"
          >
            <Send size={14} /> 검수 요청
          </button>
        )}
        <button
          onClick={onDelete}
          disabled={!deletable}
          className="flex items-center gap-1 rounded border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-40"
        >
          <Trash2 size={14} /> 삭제
        </button>
      </div>
    </div>
  );
}

function UpsertModal({
  initial, onClose, onSaved,
}: { initial: AligoTemplate | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const isNew = initial === null;
  const [form, setForm] = useState<AligoTemplateUpsertReq>({
    templateName: initial?.templtName ?? '',
    templateContent: initial?.templtContent ?? '',
    templateType: initial?.templateType ?? 'BA',
    emphasisType: initial?.templateEmType ?? 'NONE',
    emphasisTitle: initial?.templtTitle ?? '',
    emphasisSubtitle: initial?.templtSubtitle ?? '',
    secureYn: 'N',
    buttonsJson:
      initial?.buttons && initial.buttons.length > 0
        ? JSON.stringify({ button: initial.buttons }, null, 2)
        : '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 반려 상태 수정일 때만 활성 — 저장 직후 자동으로 재검수 요청
  const [reRequestOnSave, setReRequestOnSave] = useState<boolean>(
    initial?.inspStatus === 'REJ'
  );

  const nameValid = useMemo(() => NAME_REGEX.test(form.templateName ?? ''), [form.templateName]);

  const submit = async () => {
    setErr(null);
    if (!nameValid) {
      setErr('템플릿명은 3~60자여야 합니다.');
      return;
    }
    if (!form.templateContent.trim()) {
      setErr('본문은 필수입니다.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.buttonsJson?.trim()) {
        try { JSON.parse(payload.buttonsJson); } catch {
          setErr('버튼 JSON 형식이 올바르지 않습니다.'); setSaving(false); return;
        }
      } else {
        payload.buttonsJson = undefined;
      }
      if (isNew) {
        await kakaoAlimTemplateApi.create(payload);
      } else if (reRequestOnSave) {
        // BE 에서 modify + request 를 한 트랜잭션으로 처리 (지연 완충 포함)
        // → 두 번째 클릭 없이도 재검수까지 한 번에 완료
        await kakaoAlimTemplateApi.updateAndRequestReview(initial!.templtCode, payload);
      } else {
        await kakaoAlimTemplateApi.update(initial!.templtCode, payload);
      }
      await onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? e?.message ?? '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h3 className="text-lg font-bold">{isNew ? '신규 템플릿 등록' : '템플릿 수정'}</h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100"><X size={18} /></button>
        </div>

        <div className="max-h-[75vh] space-y-3 overflow-y-auto p-4">
          {err && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
          )}

          {/* 반려 사유 힌트 — 수정 참고 */}
          {initial?.inspStatus === 'REJ' && initial.comments && initial.comments.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm">
              <div className="font-semibold text-red-700 mb-1">📌 심의 반려 사유 (수정 참고)</div>
              <ul className="list-disc pl-4 space-y-0.5 text-gray-800">
                {initial.comments.map((c, i) => (
                  <li key={i}>{c.content ?? '(사유 없음)'}</li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              템플릿명 (= 우리 식별 코드) *
            </label>
            <input
              type="text"
              value={form.templateName}
              onChange={(e) => setForm({ ...form, templateName: e.target.value })}
              disabled={!isNew}
              placeholder="USER_RESERVE_APPROVED 또는 예약확정 등"
              className={`w-full rounded border px-3 py-2 text-sm ${
                nameValid ? 'border-gray-300' : 'border-red-300'
              } disabled:bg-gray-100`}
            />
            <p className="mt-1 text-xs text-gray-400">
              3~60자 (한글/영문/숫자/특수문자 허용). BE 트리거에서 이 이름으로 발송 매칭됩니다.
              (수정 시 변경 불가)
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">본문 *</label>
            <textarea
              value={form.templateContent}
              onChange={(e) => setForm({ ...form, templateContent: e.target.value })}
              rows={8}
              placeholder={`[멍투데이] 예약 확정\n#{companyName}에서 예약이 확정되었어요.`}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono"
            />
            <p className="mt-1 text-xs text-gray-400">변수는 #&#123;변수명&#125; 형식 사용</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">유형</label>
              <select
                value={form.templateType ?? 'BA'}
                onChange={(e) => setForm({ ...form, templateType: e.target.value })}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="BA">기본형 (BA)</option>
                <option value="EX">부가정보형 (EX)</option>
                <option value="AD">광고추가형 (AD)</option>
                <option value="MI">복합형 (MI)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">강조 유형</label>
              <select
                value={form.emphasisType ?? 'NONE'}
                onChange={(e) => setForm({ ...form, emphasisType: e.target.value })}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="NONE">선택안함</option>
                <option value="TEXT">강조표기형</option>
                <option value="IMAGE">이미지형</option>
              </select>
            </div>
          </div>

          {form.emphasisType === 'TEXT' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">강조 핵심정보</label>
                <input
                  type="text"
                  value={form.emphasisTitle ?? ''}
                  onChange={(e) => setForm({ ...form, emphasisTitle: e.target.value })}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">강조 보조문구</label>
                <input
                  type="text"
                  value={form.emphasisSubtitle ?? ''}
                  onChange={(e) => setForm({ ...form, emphasisSubtitle: e.target.value })}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              버튼 JSON (선택)
            </label>
            <textarea
              value={form.buttonsJson ?? ''}
              onChange={(e) => setForm({ ...form, buttonsJson: e.target.value })}
              rows={6}
              placeholder={`{
  "button": [
    { "name": "예약 확인", "linkType": "WL", "linkTypeName": "웹링크",
      "linkMo": "https://mungtoday.com/r/#{reserveId}",
      "linkPc": "https://mungtoday.com/r/#{reserveId}" }
  ]
}`}
              className="w-full rounded border border-gray-300 px-3 py-2 text-xs font-mono"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
          {/* 재검수 옵션 — 반려 수정 시만 표시 */}
          {!isNew && initial?.inspStatus === 'REJ' ? (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={reRequestOnSave}
                onChange={(e) => setReRequestOnSave(e.target.checked)}
                className="rounded"
              />
              저장 후 자동 재검수 요청
            </label>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
            >취소</button>
            <button
              onClick={submit}
              disabled={saving}
              className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving
                ? '저장 중...'
                : isNew
                ? '등록'
                : reRequestOnSave
                ? '저장 + 재검수'
                : '수정'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
